import { cn, getTestId } from '../utils';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Popover as HeadlessPopover,
  PopoverButton,
  PopoverPanel,
  Transition
} from '@headlessui/react';
import React from 'react';
import { AnchorTo } from '../types/Anchor';
import { Icon } from './icons/Icon';

type BasePopoverAttributes = Pick<React.HTMLAttributes<HTMLDivElement>, 'id' | 'className'>;

const PopoverPanelVariants = cva(
  `bg-background-input border !border-border-base rounded-lg shadow-lg absolute z-50 mt-sm overflow-y-auto`,
  {
    variants: {
      size: {
        sm: 'w-[160px]',
        md: 'w-[240px]',
        lg: 'w-[360px]',
        xl: 'w-[480px]'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

type PopoverPanelVariantProps = VariantProps<typeof PopoverPanelVariants>;

/**
 * Popover component for displaying custom content in a floating panel
 * Built on Headless UI's Popover with similar styling to Dropdown
 */
export interface PopoverProps extends BasePopoverAttributes, PopoverPanelVariantProps {
  /**
   * The trigger element that opens the popover
   */
  triggerButton: React.ReactNode;

  /**
   * Custom content to display in the popover panel
   * Can be a ReactNode or a function that receives { close }
   */
  children: React.ReactNode | ((props: { close: () => void }) => React.ReactNode);

  /**
   * CSS class name for the panel
   */
  className?: string;

  /**
   * Positioning anchor for the panel
   */
  align?: AnchorTo;

  /**
   * Test ID for testing
   */
  testId?: string;

  /**
   * Show close button in top-right corner
   */
  showCloseButton?: boolean;
}

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  (
    { triggerButton, children, className, align = 'bottom', size, testId, id, showCloseButton },
    ref
  ) => {
    return (
      <HeadlessPopover ref={ref}>
        {({ close }) => (
          <>
            <PopoverButton as={React.Fragment}>{triggerButton}</PopoverButton>

            <Transition
              enter='transition ease-out duration-200'
              enterFrom='opacity-0 translate-y-1'
              enterTo='opacity-100 translate-y-0'
              leave='transition ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-1'
            >
              <PopoverPanel
                anchor={align}
                data-testid={getTestId({ testId, id })}
                className={cn(className, PopoverPanelVariants({ size }), 'relative')}
              >
                {/* Close button in top-right corner */}
                {showCloseButton && (
                  <button
                    type='button'
                    onClick={close}
                    className='absolute top-2 right-2 p-xs rounded hover:bg-background-muted transition-colors duration-200 z-10'
                    aria-label='Close'
                    data-testid={getTestId({ testId: testId ? `${testId}-close` : undefined, id })}
                  >
                    <Icon name='xmark' className='w-4 h-4 text-foreground-muted' />
                  </button>
                )}

                {/* Pass close function to children via context if needed */}
                {typeof children === 'function' ? children({ close }) : children}
              </PopoverPanel>
            </Transition>
          </>
        )}
      </HeadlessPopover>
    );
  }
);

Popover.displayName = 'Popover';

export { Popover as default };
